import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Disc3, Mic2, Music4, PenLine, Globe, Clock } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { Discussion } from "../components/Discussion";
import { useTranslate } from "../context/TranslateContext";
import { getSong, getPerson, getMovie, getAlbum, albumForSong, SONGS } from "../mock/data";

export default function SongDetail() {
  const { id } = useParams();
  const { t } = useTranslate();
  const s = getSong(id);
  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  if (!s) return (<div><InnerNav /><div className="p-20 text-center font-body text-ash">{t("Song not found.")}</div></div>);

  const singer = getPerson(s.singer), composer = getPerson(s.composer), lyricist = getPerson(s.lyricist);
  const movie = getMovie(s.movie), album = albumForSong(s.id);
  const cover = movie?.image || album?.cover;
  const listenText = `${s.title}, sung by ${singer?.name}, composed by ${composer?.name}.`;
  const related = SONGS.filter((o) => o.id !== s.id && (o.movie === s.movie || o.singer === s.singer)).slice(0, 4);

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 py-14 md:px-12 md:py-16 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="overflow-hidden rounded-2xl border border-edge shadow-2xl">
            <img src={cover} alt={s.title} data-testid="song-cover" className="aspect-square w-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-8">
          <Overline>{t(s.genre)} · {s.language}</Overline>
          <h1 data-testid="song-title" className="mt-3 font-display text-5xl font-semibold leading-none tracking-tight text-cream md:text-7xl">{s.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 font-body text-sm text-ash">
            <span className="flex items-center gap-1.5"><Globe className="h-4 w-4 text-gold" />{s.language}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-gold" />{s.duration}</span>
            <span>{s.year}</span>
            <span className="rounded-full border border-edge px-3 py-0.5">{t(s.mood)}</span>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button data-testid="song-play" className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-body font-medium text-ink transition-transform hover:scale-105">
              <Play className="h-4 w-4 fill-ink" /> {t("Play")}
            </button>
            
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Credit icon={Mic2} role="Singer" person={singer} />
            <Credit icon={Music4} role="Composer" person={composer} />
            <Credit icon={PenLine} role="Lyricist" person={lyricist} />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {movie && (
              <Link to={`/movie/${movie.id}`} data-testid="song-from-movie" className="inline-flex items-center gap-2 rounded-xl border border-edge bg-surface/50 px-4 py-3 font-body text-sm text-cream hover:border-gold">
                <Disc3 className="h-4 w-4 text-gold" /> {t("From the film")}: <span className="text-gold">{movie.title}</span>
              </Link>
            )}
            {album && (
              <Link to={`/album/${album.id}`} data-testid="song-from-album" className="inline-flex items-center gap-2 rounded-xl border border-edge bg-surface/50 px-4 py-3 font-body text-sm text-cream hover:border-gold">
                <Disc3 className="h-4 w-4 text-gold" /> {t("Album")}: <span className="text-gold">{album.title}</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <div className="mx-auto max-w-[1400px] px-6 pb-28 md:px-12">
          <h2 className="mb-6 font-display text-3xl font-semibold text-cream">{t("Related Songs")}</h2>
          <div className="rounded-2xl border border-edge bg-surface/40">
            {related.map((o) => { const os = getPerson(o.singer); return (
              <Link key={o.id} to={`/song/${o.id}`} data-testid={`related-song-${o.id}`}
                className="flex items-center gap-4 border-b border-edge px-5 py-4 last:border-0 hover:bg-black/5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-edge text-gold"><Play className="h-3.5 w-3.5 fill-current" /></span>
                <span className="min-w-0 flex-1"><span className="block truncate font-body text-cream">{o.title}</span>
                  <span className="block truncate font-body text-sm text-ash">{os?.name} · {t(o.genre)}</span></span>
                <span className="font-body text-sm text-ash">{o.duration}</span>
              </Link>
            ); })}
          </div>
        </div>
      )}
      <div className="border-t border-edge bg-paper">
        <Discussion entityType="song" entityId={s.id} title="Discussion" />
      </div>
    </div>
  );
}

const Credit = ({ icon: Icon, role, person }) => {
  const { t } = useTranslate();
  if (!person) return null;
  return (
    <Link to={`/person/${person.id}`} data-testid={`credit-${role.toLowerCase()}`}
      className="group flex items-center gap-3 rounded-xl border border-edge bg-surface/50 p-3 transition-colors hover:border-gold">
      <img src={person.image} alt={person.name} className="h-12 w-12 rounded-full object-cover" />
      <span className="min-w-0">
        <span className="flex items-center gap-1 font-body text-xs uppercase tracking-widest text-gold"><Icon className="h-3 w-3" /> {t(role)}</span>
        <span className="block truncate font-body text-cream group-hover:text-gold">{person.name}</span>
      </span>
    </Link>
  );
};
